import React from "react";
//#region types
import { NavListItemProps } from "models/propTypes/navListPropTypes";
import { TranslationKeys } from "translations";
import { NavigationListProps } from "models/propTypes/navListPropTypes";
import {
  IconDefinitionConstants,
  NavigationListConstants,
  RouteConstants,
  SettingsConstants,
} from "utils/constants";
//#endregion
//#region components
import NavigationList from "./navigationList";
import { useTimeReportingProjectService, useBusinessUnitService } from "serviceHooks";
import { useAdvisorPermissions } from "components/advisor";
import { useInvoicePermissions } from "components/invoice";
import { useIncomingInvoicePermissions } from "components/incomingInvoice";
import { useReceiptPermissions } from "components/receipt";
import { useCashReconciliationPermissions } from "components/cashReconciliation";
import { useSalaryPermissions } from "components/salary";
import { useCheckRelations } from "components/common";
//#endregion
import { useSelector } from "react-redux";
import { AppState } from "store";
import { UrlHelper } from "utils/helpers";
import { CompanyService } from "services";

const NavigationListContainer: React.FC<NavigationListProps> = (props) => {
  const timeReportingProjectService = useTimeReportingProjectService();
  const businessUnitService = useBusinessUnitService();

  const cashReconciliationPermissions = useCashReconciliationPermissions();
  const { canUseInvoices, canUseRecurringInvoices } = useInvoicePermissions();
  const { canViewIncomingInvoices } = useIncomingInvoicePermissions();
  const { advisorViewsAvailable } = useAdvisorPermissions();
  const { receiptViewer } = useReceiptPermissions();
  const { isSalaryDeviationsAvailable, isSalaryAdmin, isAnyCommissionSalaryAvailable, isWintSalaryDeviationsAvailable } =
    useSalaryPermissions();
  const { getIsAdmin, getIsRevisor } = useCheckRelations();
  const isAdmin = getIsAdmin();
  const isRevisor = getIsRevisor();
  const isAuditorAssistant = useSelector((state: AppState) => state.auth.selectedCompany.loggedInUserIsAuditorAssistant);

  const navListTop: Array<NavListItemProps> = [
    {
      id: NavigationListConstants.DashboardId,
      displayText: isRevisor || isAuditorAssistant ? TranslationKeys.Reports.RevisorView : TranslationKeys.Common.Dashboard,
      icon: IconDefinitionConstants.WDashboard,
      path: UrlHelper.formatPath(""),
    },
    {
      id: NavigationListConstants.AdvisoryViewId,
      displayText: TranslationKeys.Reports.AdvisoryView,
      icon: IconDefinitionConstants.WCompany,
      path: UrlHelper.formatPath("/AdvisoryView"),
      activeWithDirectChild: true,
      hidden: !advisorViewsAvailable,
    },
    {
      id: NavigationListConstants.ReportsId,
      displayText: TranslationKeys.Common.Reports,
      icon: IconDefinitionConstants.WReports,
      path: UrlHelper.formatPath("/Reports"),
      activeWithDirectChild: true,
    },
    {
      id: NavigationListConstants.RevenueId,
      displayText: TranslationKeys.Common.Revenue,
      icon: IconDefinitionConstants.WIncome,
      path: UrlHelper.formatPath("/Reports/Revenue"),
      hidden:
        !canUseInvoices &&
        !canUseRecurringInvoices &&
        !timeReportingProjectService.hasReadOnlyPermissions() &&
        !cashReconciliationPermissions.canViewCashReconciliations(),
      children: [
        {
          id: NavigationListConstants.InvoicesId,
          displayText: TranslationKeys.Invoice.CustomerInvoices,
          icon: IconDefinitionConstants.WPaperPlane,
          path: "/Invoices",
          hidden: !canUseInvoices,
        },
        {
          id: NavigationListConstants.RecurringInvoicesId,
          displayText: TranslationKeys.Invoice.RecurringInvoices,
          icon: IconDefinitionConstants.WRepeat,
          path: "/RecurringInvoices",
          hidden: !canUseRecurringInvoices,
        },
        {
          id: NavigationListConstants.TimeReportingProjectsId,
          displayText: TranslationKeys.TimeReportingProject.TimeReportingProjects,
          icon: IconDefinitionConstants.WFolder,
          path: "/Projects",
          hidden: !(
            CompanyService.isTimeReportingEnabled() &&
            (timeReportingProjectService.hasEditPermissions() || timeReportingProjectService.hasReadOnlyPermissions())
          ),
        },
        {
          id: NavigationListConstants.CashReconciliationsId,
          displayText: TranslationKeys.CashReconciliation.CashReconciliations,
          icon: IconDefinitionConstants.WCalculator,
          path: "/CashReconciliations",
          hidden: !cashReconciliationPermissions.canViewCashReconciliations(),
        },
      ],
    },
    {
      id: NavigationListConstants.ExpensesId,
      displayText: TranslationKeys.Common.Expenses,
      icon: IconDefinitionConstants.WCosts,
      path: UrlHelper.formatPath("/Reports/Expenses"),
      hidden: !canViewIncomingInvoices && !receiptViewer,
      children: [
        {
          id: NavigationListConstants.IncomingInvoicesId,
          displayText: TranslationKeys.Reports.IncomingInvoices,
          icon: IconDefinitionConstants.WInvoiceSupplier,
          path: "/IncomingInvoices",
          hidden: !canViewIncomingInvoices,
        },
        {
          id: NavigationListConstants.ReceiptsId,
          displayText: TranslationKeys.Reports.Receipts,
          icon: IconDefinitionConstants.WReceipt,
          path: "/Receipts",
          hidden: !receiptViewer,
        },
      ],
    },
    {
      id: NavigationListConstants.EmployeesId,
      displayText: TranslationKeys.Company.Employees,
      icon: IconDefinitionConstants.WPerson,
      path: UrlHelper.formatPath("/Employees"),
      children: [
        {
          id: NavigationListConstants.SalariesId,
          displayText: TranslationKeys.Company.Salaries,
          icon: IconDefinitionConstants.WContact,
          path: "/Salaries",
          hidden: isWintSalaryDeviationsAvailable,
        },
        {
          id: NavigationListConstants.WintSalariesId,
          displayText: TranslationKeys.Company.Salaries,
          icon: IconDefinitionConstants.WContact,
          path: "/WintSalaries",
          hidden: !isWintSalaryDeviationsAvailable,
        },
        {
          id: NavigationListConstants.DeviationsId,
          displayText: TranslationKeys.Company.Deviations,
          icon: IconDefinitionConstants.WCalendar,
          path: "/Deviations",
          hidden: !isSalaryDeviationsAvailable,
        },
        {
          id: NavigationListConstants.WintDeviationsId,
          displayText: TranslationKeys.Company.Deviations,
          icon: IconDefinitionConstants.WCalendar,
          path: "/WintDeviations",
          hidden: !isWintSalaryDeviationsAvailable,
        },
        {
          id: NavigationListConstants.CommissionsId,
          displayText: TranslationKeys.Company.Commissions,
          icon: IconDefinitionConstants.WSplitSection,
          path: "/Commission",
          hidden: !isAnyCommissionSalaryAvailable,
        },
        {
          id: NavigationListConstants.BusinessUnitsId,
          displayText: TranslationKeys.BusinessUnit.BusinessUnits,
          icon: IconDefinitionConstants.WCustomer,
          path: "/BusinessUnits",
          hidden: !(businessUnitService.isBusinessUnitsAvailable() && businessUnitService.hasEditPermissions()),
        },
        {
          id: NavigationListConstants.SalarySpecificationsId,
          displayText: TranslationKeys.Salary.SalarySpecifications,
          icon: IconDefinitionConstants.WContact,
          path: "/Salaries/Specifications",
          hidden: !(isSalaryAdmin || isAdmin),
        },
        {
          id: NavigationListConstants.BenefitsId,
          displayText: TranslationKeys.Benefit.BenefitsTitle,
          icon: IconDefinitionConstants.WGifts,
          path: "/Benefits",
          hidden: !isWintSalaryDeviationsAvailable,
        },
      ],
    },
  ];

  const navListBottom: Array<NavListItemProps> = [
    {
      id: NavigationListConstants.SupportId,
      displayText: TranslationKeys.Common.Support,
      icon: IconDefinitionConstants.WCommentDots,
      path: UrlHelper.formatPath("/Support"),
    },
    {
      id: NavigationListConstants.SettingsId,
      displayText: TranslationKeys.Common.Settings,
      icon: IconDefinitionConstants.WSettings,
      path: UrlHelper.formatPath(
        `/Settings?${RouteConstants.QueryParamStrings.Section}=${SettingsConstants.NavigationKey.Company}`
      ),
    },
  ];

  return <NavigationList topNavListItems={navListTop} bottomNavListItems={navListBottom} collapsed={props.collapsed} />;
};

export default NavigationListContainer;
